import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { Alert, EmptyState, IconButton, Spinner } from '../../components/ui.tsx';
import { BRAND, BrandIcon, FileTypeIcon } from '../../components/icons.tsx';
import { Tooltip } from '../../components/Tooltip.tsx';
import { formatBytes, formatRelative, pluralise } from '../../lib/display.ts';
import { StarGlyph } from './StarButton.tsx';

/**
 * The eight or ten things that are alive right now.
 *
 * A Drive is a filing cabinet and this is the desk: the ledger being worked
 * on, the letterhead everything is printed on, the invoice somebody is
 * arguing about. They live in different folders and the only thing they have
 * in common is that somebody said they matter this week.
 *
 * Every kind in one list, because that is the point — a person looking for
 * "the thing I had open yesterday" does not first remember whether it was a
 * sheet or a scan.
 */

type Kind = 'file' | 'folder' | 'spreadsheet' | 'document';

interface StarredItem {
  kind: Kind;
  id: string;
  name: string;
  path: string;
  updatedAt: string;
  mimeType?: string;
  sizeBytes?: number;
}

export function StarredPage() {
  const navigate = useNavigate();
  const queries = useQueryClient();

  const starred = useQuery({
    queryKey: ['starred'],
    queryFn: () => api.get<StarredItem[]>('/api/drive/starred'),
  });

  const unstar = useMutation({
    mutationFn: (item: StarredItem) =>
      api.post(`/api/drive/starred/${item.kind}/${item.id}`, { isStarred: false }),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ['starred'] });
      void queries.invalidateQueries({ queryKey: ['drive'] });
    },
  });

  if (starred.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
        <Spinner /> Loading…
      </div>
    );
  }

  if (starred.isError) return <Alert>Could not load your starred items.</Alert>;

  const items = starred.data ?? [];

  /** Where clicking one takes you. A folder opens in the Drive. */
  const open = (item: StarredItem) => {
    if (item.kind === 'spreadsheet') navigate(`/sheets/${item.id}`);
    else if (item.kind === 'document') navigate(`/documents/${item.id}`);
    else navigate(`/drive?folder=${item.kind === 'folder' ? item.id : ''}`);
  };

  return (
    <div className="h-full overflow-y-auto">
      <header className="border-b border-border pb-4">
        <h1 className="text-h1 text-ink">Starred</h1>
        <p className="mt-0.5 text-meta text-muted">
          {items.length === 0
            ? 'Nothing starred yet'
            : `${pluralise(items.length, 'item')}, most recently worked on first`}
        </p>
      </header>

      {items.length === 0 ? (
        <EmptyState
          title="Nothing starred yet"
          description="Star the things you are working on — a ledger, a letterhead, an invoice — and they will be here whatever folder they live in. Use the star in a row's menu, or the one that appears when you hover over it."
          action={
            <Link
              to="/drive"
              className="rounded-md bg-[var(--blue-600)] px-3 py-1.5 text-body-sm text-white"
            >
              Open the Drive
            </Link>
          }
        />
      ) : (
        <ul className="mt-4 overflow-hidden rounded-lg border border-border-strong bg-surface">
          {items.map((item) => (
            <li
              key={`${item.kind}-${item.id}`}
              className="group flex cursor-pointer items-center gap-3 border-b border-border px-4 py-2.5 transition-fast last:border-0 hover:bg-blue-50"
              onClick={() => open(item)}
            >
              <span className="shrink-0" aria-hidden>
                {item.kind === 'folder' ? (
                  <BrandIcon src={BRAND.folder} size={20} />
                ) : item.kind === 'spreadsheet' ? (
                  <BrandIcon src={BRAND.excel} size={20} />
                ) : item.kind === 'document' ? (
                  <BrandIcon src={BRAND.word} size={20} />
                ) : (
                  <FileTypeIcon name={item.name} mimeType={item.mimeType ?? ''} />
                )}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-body-sm text-ink">{item.name}</span>
                <span className="block truncate text-micro text-muted">
                  {item.path || '/'}
                  {item.sizeBytes === undefined ? '' : ` · ${formatBytes(item.sizeBytes)}`}
                </span>
              </span>

              <span className="hidden shrink-0 text-micro text-muted sm:block">
                {formatRelative(item.updatedAt)}
              </span>

              {/* The one action this page needs: taking the star off. Doing it
                  from here is how a starred list stops growing for ever. */}
              <Tooltip label="Remove the star">
                <IconButton
                  label={`Remove the star from ${item.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    unstar.mutate(item);
                  }}
                >
                  <StarGlyph filled />
                </IconButton>
              </Tooltip>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
